import { Navigate, Outlet, useLocation } from "react-router";

import { ErrorState, HelpdeskContactLine, LoadingState } from "@/components/common";
import { useMe } from "@/lib/api";
import { logger } from "@/lib/logger";
import { loginPathFor } from "@/lib/safe-return-path";
import { isSessionRejected } from "@/lib/session-error";

/**
 * The login URL for this visit, carrying where the visitor was going as `next`.
 *
 * A signed-out reader following a link out of an Ivanti email (`/t/12345`)
 * must land back on that ticket after signing in, not on the front page.
 * `loginPathFor` leaves `next` off for the root and for anything it would not
 * accept back, so the login page never receives an unsafe value from us.
 */
function useLoginRedirect(): string {
  const location = useLocation();
  return loginPathFor(`${location.pathname}${location.search}${location.hash}`);
}

export function ProtectedRoute() {
  const { data: me, isLoading, isFetching, error, refetch } = useMe();
  const loginPath = useLoginRedirect();

  if (
    isLoading ||
    (error !== null && !isSessionRejected(error) && me === undefined && isFetching)
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {/* `LoadingState` names the spinner from `state.loading`, so the first
            thing a screen reader hears on a cold load comes from the catalogue
            rather than the design system's English default. */}
        <LoadingState description={null} />
      </div>
    );
  }

  // Only a 401/403 means the session is gone. A network error or a 5xx (the
  // backend restarting) has already been retried by `useMe`; bouncing to
  // /login for it would sign everyone out on a server hiccup, so say what
  // happened and offer a retry instead.
  if (error !== null && !isSessionRejected(error)) {
    if (me !== undefined) {
      // A failed background re-check with a session already known: keep the
      // page rather than replace it with an error.
      logger.warn("[auth] could not re-check the session; keeping the page", error);
      return <Outlet context={{ me }} />;
    }
    logger.warn("[auth] could not check the session; not redirecting", error);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <ErrorState error={error} onRetry={() => void refetch()} className="max-w-lg" />
        <HelpdeskContactLine />
      </div>
    );
  }

  if (error !== null || !me) {
    logger.debug("[auth] no session; redirecting to", loginPath);
    return <Navigate to={loginPath} replace />;
  }

  return <Outlet context={{ me }} />;
}

export function AdminRoute() {
  const { data: me, isLoading } = useMe();
  const loginPath = useLoginRedirect();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {/* `LoadingState` names the spinner from `state.loading`, so the first
            thing a screen reader hears on a cold load comes from the catalogue
            rather than the design system's English default. */}
        <LoadingState description={null} />
      </div>
    );
  }

  if (!me) {
    logger.debug("[auth] no session on an admin route; redirecting to", loginPath);
    return <Navigate to={loginPath} replace />;
  }

  if (me.role !== "admin") {
    return <Navigate to="/home" replace />;
  }

  return <Outlet context={{ me }} />;
}

/**
 * Migrators (and admins) only — the Migrate area.
 *
 * Like `<AdminRoute>`, a convenience: every `/migrate/*` endpoint is gated by
 * `require_migrator` on its own.
 */
export function MigratorRoute() {
  const { data: me, isLoading } = useMe();
  const loginPath = useLoginRedirect();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState description={null} />
      </div>
    );
  }

  if (!me) {
    logger.debug("[auth] no session on a migrator route; redirecting to", loginPath);
    return <Navigate to={loginPath} replace />;
  }

  if (me.can_migrate !== true) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet context={{ me }} />;
}

/**
 * The Teams area (`useMe().can_view_teams`): all of IT, read-only, plus admins
 * and team managers, who edit (8 Oct).
 *
 * A convenience like the others: every `/teams/*` endpoint checks on its own,
 * per team, by Employee RecId (and 404s while `TEAMS_AREA_ENABLED` is off).
 */
export function TeamsRoute() {
  const { data: me, isLoading } = useMe();
  const loginPath = useLoginRedirect();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState description={null} />
      </div>
    );
  }

  if (!me) {
    logger.debug("[auth] no session on a teams route; redirecting to", loginPath);
    return <Navigate to={loginPath} replace />;
  }

  if (me.can_view_teams !== true) {
    logger.debug("[auth] not IT, an admin or a team manager; leaving the teams area");
    return <Navigate to="/home" replace />;
  }

  return <Outlet context={{ me }} />;
}

/**
 * IT Admin → Management → Approvers (`useMe().approvers`): HelpDesk admins and,
 * since 12 Oct, members of an APPROVERS_EDITOR_TEAMS Ivanti team ("Backoffice
 * Team"), all with full edit.
 *
 * A convenience like the others: every `/admin/approvers/*` endpoint applies
 * the same rule on its own (403 otherwise).
 */
export function ApproversRoute() {
  const { data: me, isLoading } = useMe();
  const loginPath = useLoginRedirect();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState description={null} />
      </div>
    );
  }

  if (!me) {
    logger.debug("[auth] no session on the approvers route; redirecting to", loginPath);
    return <Navigate to={loginPath} replace />;
  }

  if (me.role !== "admin" && me.approvers !== true) {
    logger.debug("[auth] not an admin or an approvers editor; leaving Approvers");
    return <Navigate to="/home" replace />;
  }

  return <Outlet context={{ me }} />;
}
